import paths from "../routes/paths";
import { PAYMENT_PLAN_TYPES } from "../constants";

export const HTTP_REPAY_DECLINED_PAYMENT = 540;

export const PAYMENT_FAILURE_CONFIRM_STORAGE_KEY = "paymentFailure:v1:confirm";
export const PAYMENT_FAILURE_PLAN_STORAGE_KEY = "paymentFailure:v1:plan";

export const PAYMENT_FAILURE_TYPES = {
  declined: "declined",
  charged_plan_failed: "charged_plan_failed",
  unavailable: "unavailable",
};

export const shouldRetryPlanWithoutNewCharge = (_failure, hasCapturedSale) =>
  Boolean(hasCapturedSale);

export const resolveCardDeclineRetryStep = ({
  planType,
  isGoodFaithOffer,
} = {}) => {
  const retryToSetup =
    planType === PAYMENT_PLAN_TYPES.payInFull ||
    planType === PAYMENT_PLAN_TYPES.settleInFull ||
    (planType === PAYMENT_PLAN_TYPES.paymentPartial &&
      Boolean(isGoodFaithOffer));
  return retryToSetup ? "setup" : "initial";
};

export const UNKNOWN_PAYMENT_FAILURE = {
  canRetry: false,
  failureType: "unknown",
};

const getErrorStatus = (error) =>
  Number(error?.response?.status || error?.status || 0);

const getErrorMessage = (error) =>
  String(
    error?.response?.data?.msg ||
      error?.response?.data?.message ||
      error?.msg ||
      error?.message ||
      ""
  );

export const isRepayDeclinedPaymentError = (error) => {
  const status = getErrorStatus(error);
  if (status === HTTP_REPAY_DECLINED_PAYMENT) return true;
  if (status >= 500 || status === 0) return false;
  return getErrorMessage(error).includes("RepayDeclinedPaymentException");
};

export const resolvePaymentFailureState = (error) => {
  const status = getErrorStatus(error);
  if (status === HTTP_REPAY_DECLINED_PAYMENT) {
    return { canRetry: true, failureType: "declined" };
  }
  if (status === 550) {
    return { canRetry: true, failureType: "charged_plan_failed" };
  }
  if (status === 530) {
    return {
      canRetry: error?.response?.data?.retryable === true,
      failureType: "unavailable",
    };
  }
  if (status >= 500 || status === 0) {
    return null;
  }

  if (isRepayDeclinedPaymentError(error)) {
    return { canRetry: true, failureType: "declined" };
  }

  const retryable = error?.response?.data?.retryable;
  if (typeof retryable === "boolean") {
    return {
      canRetry: retryable,
      failureType: retryable ? "declined" : "unknown",
    };
  }

  return null;
};

export const resolvePaymentPlanCreateFailurePath = (
  _error,
  { isShortLink } = {}
) => (isShortLink ? paths.specialOfferError : paths.accountError);

export const parseStoredPaymentFailure = (raw) => {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    return {
      canRetry: parsed.canRetry === true,
      failureType: parsed.failureType || UNKNOWN_PAYMENT_FAILURE.failureType,
    };
  } catch (_error) {
    return null;
  }
};

export const readStoredPaymentFailure = (storageKey) => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return parseStoredPaymentFailure(window.sessionStorage.getItem(storageKey));
  } catch (_error) {
    return null;
  }
};

export const writeStoredPaymentFailure = (storageKey, nextFailure) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (!nextFailure) {
      window.sessionStorage.removeItem(storageKey);
      return;
    }

    window.sessionStorage.setItem(storageKey, JSON.stringify(nextFailure));
  } catch (_error) {
    // ignore quota / private mode failures
  }
};

export const getPaymentConfirmScreen = ({ paymentFailed, paymentMethod }) => {
  if (paymentFailed) {
    return "failure";
  }

  if (!paymentMethod?.type) {
    return "leaving";
  }

  return "confirm";
};

export const leavePaymentConfirmAfterFailure = ({
  clearStoredFailure,
  clearPaymentMethod,
  setSelectedStep,
  replaceUrl,
  historyBack,
  comesFromSplashOrFrictionless,
  paymentPlanPath,
}) => {
  clearStoredFailure();
  clearPaymentMethod();
  setSelectedStep("setup");

  if (comesFromSplashOrFrictionless) {
    historyBack();
    return { keepFailureScreen: true, navigate: "back" };
  }

  replaceUrl(paymentPlanPath);
  return { keepFailureScreen: true, navigate: "payment-plan" };
};
